import type { SupabaseClient } from "@supabase/supabase-js";
import { AccountBroadcastManager } from "./AccountBroadcastManager";
import { DatabaseAdapter } from "./adapters/DatabaseAdapter";
import SupabaseDatabaseAdapter from "./adapters/SupabaseDatabaseAdapter";
import { resetSupabaseRealtimeAuthForTests } from "./wireSupabaseRealtimeAuth";

export * from "./accountBroadcastDispatch";
export * from "./clientDeviceToken";
export * from "./clientOriginToken";
export * from "./collection";
export * from "./AccountBroadcastManager";
export * from "./config";
export * from "./errors";
export * from "./List";
export * from "./model";
export * from "./modelRegistry";
export * from "./query";
export * from "./reference";
export * from "./setupModelRelations";
export * from "./subscribeList";
export * from "./validation";

// Export adapter types and classes
export { DatabaseAdapter } from "./adapters/DatabaseAdapter";
export type {
  CountResponse,
  DataResponse,
  SingleResponse,
} from "./adapters/DatabaseAdapter";

/** How SubscriptionManager receives server realtime updates. */
export type RealtimeMode = "hybrid" | "postgres_changes";

interface OOMConfig {
  adapter: DatabaseAdapter | null;
  exceptionHandler: (error: Error, extra?: Record<string, any>) => void;
  /** Current account for private Realtime broadcast channels. */
  accountId: string | null;
  /** Supabase client for broadcast when the DB adapter is not Supabase-backed (e.g. Tauri). */
  supabaseClient: SupabaseClient | null;
  /** Test/diagnostics hook when account broadcast subscription state changes. */
  accountBroadcastSubscribedListener?: (subscribed: boolean) => void;
  /**
   * hybrid: logged tables use account broadcast; others use postgres_changes.
   * postgres_changes: all tables use postgres_changes (portal / fallback).
   */
  realtimeMode: RealtimeMode;
}

interface OOM {
  config: OOMConfig;
  configure(config: Partial<OOMConfig>): void;
  captureException(error: Error, extra?: Record<string, any>): void;
  accountBroadcast: AccountBroadcastManager | undefined;
  setAccountId(accountId: string | null): Promise<void>;
  resetAccountBroadcastForTests(): void;
}

function resolveSupabaseClient(config: OOMConfig): SupabaseClient | null {
  if (config.supabaseClient) {
    return config.supabaseClient;
  }
  const adapter = config.adapter;
  if (adapter instanceof SupabaseDatabaseAdapter) {
    return adapter.supabase;
  }
  return null;
}

let accountBroadcastManager: AccountBroadcastManager | undefined;
let accountBroadcastSupabase: SupabaseClient | null = null;

export const oom: OOM = {
  config: {
    adapter: null,
    exceptionHandler: () => {}, // default no-op handler
    accountId: null,
    supabaseClient: null,
    realtimeMode: "hybrid",
  },

  get accountBroadcast() {
    const supabase = resolveSupabaseClient(this.config);
    if (!supabase) {
      return undefined;
    }
    if (!accountBroadcastManager || accountBroadcastSupabase !== supabase) {
      accountBroadcastManager = new AccountBroadcastManager(supabase, (subscribed) => {
        oom.config.accountBroadcastSubscribedListener?.(subscribed);
      });
      accountBroadcastSupabase = supabase;
    }
    return accountBroadcastManager;
  },

  configure(config: Partial<OOMConfig>) {
    this.config = { ...this.config, ...config };
    if (config.accountBroadcastSubscribedListener && accountBroadcastManager) {
      config.accountBroadcastSubscribedListener(accountBroadcastManager.subscribed);
    }
  },

  async setAccountId(accountId: string | null) {
    const manager = this.accountBroadcast;
    const previousAccountId = manager?.currentAccountId ?? null;
    const wasSubscribed = manager?.subscribed ?? false;
    this.config = { ...this.config, accountId };
    if (!manager) {
      return;
    }
    await manager.setAccountId(accountId);
    if (accountId) {
      if (accountId !== previousAccountId || !wasSubscribed) {
        try {
          await manager.ensureSubscribed();
        } catch (error) {
          const err = error instanceof Error ? error : new Error(String(error));
          console.error("Account broadcast subscription failed:", err);
          this.captureException(err, { accountId });
        }
      }
    } else {
      await manager.unsubscribe();
    }
  },

  resetAccountBroadcastForTests() {
    accountBroadcastManager = undefined;
    accountBroadcastSupabase = null;
    this.config.accountId = null;
    this.config.accountBroadcastSubscribedListener?.(false);
    resetSupabaseRealtimeAuthForTests();
  },

  captureException(error: Error, extra?: Record<string, any>) {
    this.config.exceptionHandler(error, extra);
  },
};
